'use client';

import { Icon } from '../icon';
import { IconTile, SectionHead, toneVar, useReveal } from '../primitives';
import { audiences, href, products, type Product } from '../data';

function ProductCard({ p }: { p: Product }) {
  const c = toneVar(p.tone);
  return (
    <div className="card prod-card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14, position: 'relative', overflow: 'hidden' }}>
      <div className="between" style={{ alignItems: 'flex-start' }}>
        <IconTile name={p.icon} tone={p.tone} />
        <span className="tag" style={{ color: c, borderColor: `color-mix(in oklch, ${c} 38%, transparent)`, background: `color-mix(in oklch, ${c} 10%, transparent)` }}>{p.stage}</span>
      </div>
      <div className="stack" style={{ gap: 3 }}>
        <div className="row" style={{ gap: 9, flexWrap: 'wrap' }}>
          <h3 className="h4" style={{ fontSize: 20 }}>{p.name}</h3>
        </div>
        <span className="mono subtle" style={{ fontSize: 12 }}>{p.alias}</span>
      </div>
      <p style={{ margin: 0, fontSize: 14, color: 'var(--fg-soft)', fontWeight: 500 }}>{p.tagline}</p>
      <p className="muted pretty" style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55 }}>{p.desc}</p>
      <div className="row wrap" style={{ gap: 7, marginTop: 'auto', paddingTop: 6 }}>
        {p.meta.map((m) => <span key={m} className="kbd" style={{ fontSize: 11 }}>{m}</span>)}
      </div>
    </div>
  );
}

export function ProductsSection() {
  useReveal();
  return (
    <section className="section" id="products" style={{ background: 'var(--bg-sunken)', borderBlock: '1px solid var(--line-soft)' }}>
      <div className="container">
        <div className="between wrap" style={{ gap: 24, alignItems: 'flex-end' }}>
          <SectionHead
            eyebrow="The service suite"
            title="Six services. Take only what you need."
            lead="Connected to Visa and Mastercard, each service is distinct and independently switched on. Run them together and disputes are mitigated as completely — and as cheaply — as possible."
          />
          <a href={href('enroll')} className="btn btn-ghost reveal" style={{ alignSelf: 'flex-end' }}>Configure a stack<Icon name="arrow-right" size={16} /></a>
        </div>
        <div className="prod-grid reveal" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginTop: 44 }}>
          {products.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      </div>
    </section>
  );
}

export function AudienceSection() {
  useReveal();
  return (
    <section className="section" id="audiences">
      <div className="container">
        <SectionHead align="center" eyebrow="Built for both sides of the integration"
          title="One platform, two ways to win"
          lead="Whether you’re routing volume for a portfolio of merchants or fighting your own VAMP ratio, the lifecycle meets you where you are." />
        <div className="aud-grid reveal" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 18, marginTop: 44 }}>
          {audiences.map((a) => {
            const c = toneVar(a.tone);
            return (
              <div key={a.id} className="card card-pad" style={{ display: 'flex', flexDirection: 'column', gap: 16, position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(420px 200px at 100% 0%, color-mix(in oklch, ${c} 14%, transparent), transparent 60%)`, pointerEvents: 'none' }} />
                <div className="row" style={{ gap: 14, position: 'relative' }}>
                  <IconTile name={a.icon} tone={a.tone} size={52} icon={24} />
                  <h3 className="h4" style={{ fontSize: 21 }}>{a.name}</h3>
                </div>
                <p className="muted pretty" style={{ margin: 0, fontSize: 15, position: 'relative' }}>{a.blurb}</p>
                <div className="stack" style={{ gap: 10, marginTop: 4, position: 'relative' }}>
                  {a.points.map((p, k) => (
                    <div key={k} className="row" style={{ gap: 10 }}>
                      <Icon name="circle-check" size={18} style={{ color: c, flexShrink: 0 }} />
                      <span style={{ fontSize: 14.5, color: 'var(--fg-soft)' }}>{p}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
